/**
 * Dashboard Page - AWLP Design System
 * Main landing view after onboarding.
 *
 * T19 (absorbed by UPS-30): surfaces only the feature areas the user's active
 * plan unlocks.  Entitlements come from authStore (populated at login and on
 * every refreshUser() call).  Absent module → hidden card.  READ-capped module
 * → card shown with a read-only badge.
 */

import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Users,
  Lock,
  ChevronRight,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { useAuthStore } from '../../stores/authStore';
import { useEntitlements } from '../../hooks/useEntitlements';
import type { LicensedModule } from '../../stores/authStore';

/* ── Module feature definitions ── */

interface ModuleFeature {
  module: LicensedModule;
  label: string;
  description: string;
  route: string;
  Icon: LucideIcon;
}

const MODULE_FEATURES: ModuleFeature[] = [
  {
    module: 'clients',
    label: 'Clients',
    description: 'Manage client records and track progress over time.',
    route: '/clients',
    Icon: Users,
  },
];

/* ── Styled components ── */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
`;

const FeatureCard = styled.button<{ $readonly?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[5]};
  text-align: left;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  width: 100%;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.sky};
    box-shadow: ${({ theme }) => theme.shadows.md};
    transform: translateY(-1px);
  }

  opacity: ${({ $readonly }) => ($readonly ? 0.85 : 1)};
`;

const FeatureCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const IconWrap = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.primary.lightSky};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary.base};
  flex-shrink: 0;
`;

const ReadonlyBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => theme.colors.backgrounds.light};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  padding: 2px 8px;
`;

const FeatureLabel = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const FeatureDesc = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  margin: 0;
  flex: 1;
`;

const FeatureCardFooter = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.primary.base};
`;

const EmptyModules = styled.div`
  padding: ${({ theme }) => theme.spacing[6]};
  background: ${({ theme }) => theme.colors.backgrounds.light};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  text-align: center;
`;

const GettingStartedText = styled.p`
  margin-top: ${({ theme }) => theme.spacing[4]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
`;

/* ── Component ── */

export const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { hasModule, isModuleReadonly } = useEntitlements();

  // Only surface modules the current plan grants access to.
  const entitledFeatures = MODULE_FEATURES.filter(({ module }) => hasModule(module));
  // Guard against the entitlements-not-yet-loaded window: if the user object
  // exists but entitlements haven't been fetched yet, don't flash a false
  // "no modules" empty state — show nothing in the section until they arrive.
  const entitlementsReady = user?.entitlements !== undefined;

  return (
    <Container>
      <Header>
        <Title>Welcome back, {user?.firstName}!</Title>
        <Subtitle>Here's an overview of your clinical decision support activities.</Subtitle>
      </Header>

      {/* Entitlement-gated feature shortcuts */}
      <section aria-label="Your plan features">
        <SectionTitle>Your Workspace</SectionTitle>

        {entitlementsReady && entitledFeatures.length === 0 ? (
          <EmptyModules>
            No modules are enabled on your current plan. Contact your administrator to upgrade.
          </EmptyModules>
        ) : (
          <Grid>
            {entitledFeatures.map(({ module, label, description, route, Icon }) => {
              const readonly = isModuleReadonly(module);
              return (
                <FeatureCard
                  key={module}
                  $readonly={readonly}
                  onClick={() => navigate(route)}
                  aria-label={`${label}${readonly ? ' (read-only)' : ''}`}
                  data-testid={`module-card-${module}`}
                >
                  <FeatureCardHeader>
                    <IconWrap>
                      <Icon size={20} strokeWidth={1.8} />
                    </IconWrap>
                    {readonly && (
                      <ReadonlyBadge>
                        <Lock size={10} />
                        Read-only
                      </ReadonlyBadge>
                    )}
                  </FeatureCardHeader>

                  <FeatureLabel>{label}</FeatureLabel>
                  <FeatureDesc>{description}</FeatureDesc>

                  <FeatureCardFooter>
                    Open <ChevronRight size={14} />
                  </FeatureCardFooter>
                </FeatureCard>
              );
            })}
          </Grid>
        )}
      </section>

      {/* Getting started card */}
      <Card padding="lg">
        <h3>Getting Started</h3>
        <GettingStartedText>
          Welcome to the CDSS Platform. Use the navigation menu on the left to access different
          sections of the application, or click a workspace card above to jump straight in.
        </GettingStartedText>
      </Card>
    </Container>
  );
};

export default DashboardPage;
